import React, { useState, useEffect } from 'react';
import { ChakraProvider, Box, Spinner, Flex, extendTheme } from '@chakra-ui/react';
import { supabase } from './supabaseClient';
import Dashboard from './components/Dashboard';
import AuthForm from './components/AuthForm';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/700.css';

// Custom Chakra UI theme
const theme = extendTheme({
  fonts: {
    heading: 'Inter, system-ui, sans-serif',
    body: 'Inter, system-ui, sans-serif',
  },
  styles: {
    global: {
      body: {
        bg: 'white',
        color: 'gray.800',
      },
      '*': {
        boxSizing: 'border-box',
      },
    },
  },
  colors: {
    brand: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'brand',
      },
      variants: {
        solid: {
          bg: 'gray.900',
          color: 'white',
          _hover: {
            bg: 'gray.800',
            transform: 'translateY(-1px)',
            boxShadow: 'lg',
          },
          transition: 'all 0.2s ease',
        },
        ghost: {
          _hover: {
            bg: 'gray.100',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease',
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
        },
      },
    },
  },
});

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shouldOpenChat, setShouldOpenChat] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);

  useEffect(() => {
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const openChat = urlParams.get('openChat');
    const siteId = urlParams.get('siteId');
    const embedded = urlParams.get('embedded');
    
    if (openChat === 'true' && siteId) {
      setShouldOpenChat(true);
      // Store siteId for widget mode
      window.WIDGET_SITE_ID = siteId;
    }

    if (embedded === 'true') {
      setIsEmbedded(true);
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      // It's generally a good idea to also set loading to false here,
      // but ensure it doesn't cause issues if getSession also sets it.
      // For now, keeping it as is, as getSession should handle the initial load.
      // setLoading(false); 
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      alert('Error signing out: ' + error.message);
    }
  };

    return (
      <ChakraProvider theme={theme}>
      {loading ? (
        <Flex
          minH="100vh"
          align="center"
          justify="center"
          bg="linear-gradient(135deg, #101624 0%, #1B2540 100%)"
        >
          <Spinner
            size="xl"
            color="white"
            thickness="4px"
          />
        </Flex>
      ) : (
      <Box minH="100vh" bg="white">
        {!session && !shouldOpenChat ? (
          <AuthForm />
        ) : (
          <Dashboard 
            session={session} 
            onSignOut={handleSignOut} 
            shouldOpenChat={shouldOpenChat}
            widgetSiteId={window.WIDGET_SITE_ID}
            isEmbedded={isEmbedded}
          />
        )}
      </Box>
      )}
    </ChakraProvider>
  );
}

export default App;
 